import { PAGE_SIZE } from '@/constants'
import fetcher, { fetchWithToken } from '@/services/fetcher'
import { buildQueryV2 } from '@/utils/helpers'
import {
  ICustomerReviewSummary,
  IListPostReview,
  IListPostReviewProps,
  IListReview,
  IListReviewByCustomer,
  IListReviewByCustomerProps,
  IReviewDetail,
} from '../types/review'

const QUERY_POST_REVIEW = `query GetPostsByProductUrlKey($sku: String!, $pageSize: Int, $currentPage: Int) {
  getPostsByProductUrlKey(
    sku: $sku
    pageSize: $pageSize
    currentPage: $currentPage
  ) {
    total_count
    pageInfo {
      currentPage
      endPage
      hasNextPage
      hasPreviousPage
      pageSize
      startPage
    }
    items {
      name
      publish_date
      short_description
      url_key
      image
    }
  }
}`

const QUERY_LIST_REVIEW = `query GetProductReviews($productId: Int!, $pageSize: Int, $currentPage: Int) {
  productReviews(
    productId: $productId
    pageSize: $pageSize
    currentPage: $currentPage
  ) {
    items {
      review_id
      product_id
      sku
      source
      author_name
      rating
      title
      content
      is_verified_buyer
      status
      reviewed_at
      created_at
    }
    total_count
    page_info {
      current_page
      page_size
      total_pages
    }
  }
}
`

const QUERY_LIST_REVIEWS_BY_CUSTOMER = `
query GetCustomerReviewsList($filter: CustomerReviewFilter!, $pageSize: Int, $currentPage: Int, $status: CustomerReviewStatus) {
  getCustomerReviewsList(
    filter: $filter
    pageSize: $pageSize
    currentPage: $currentPage
    status: $status
  ) {
    items_reviewed {
      customer_avatar
      description
      images
      nickname
      order_created_at
      order_received_at
      admin_response
      rating_stars
      review_created_at
      review_id
      tag_image
      tag_name
      videos
      order_items {
        quantity
        options {
          ...MonaReviewItemOptionFields
        }
      }
    }
    items_not_reviewed {
      days_left_to_review
      delivery_date
      increment_id
      order_date
      product_count
      review_params {
        increment_id
        protect_code
        refund_amount
      }
      items {
        product_name
        product_url
        quantity
        options {
          ...MonaReviewItemOptionFields
        }
        product_image {
          label
          url
        }
      }
    }
    total_count
    page_info {
      currentPage
      current_page
      endPage
      hasNextPage
      hasPreviousPage
      pageSize
      page_size
      startPage
      total_pages
    }
  }
}

fragment MonaReviewItemOptionFields on MonaReviewItemOption {
  label
  option_id
  option_value
  value
}
`

const QUERY_CUSTOMER_REVIEWS_SUMMARY = `
query GetCustomerReviewSummary {
  getCustomerReviewSummary {
    not_reviewed_count
    reviewed_count
    total_refund_amount
    total_reviewed_orders
    reviewed_count_cancel
    reviewed_count_confirm
    reviewed_count_pending
    rating_summary {
      count
      star
    }
  }
}
`
const QUERY_LIST_REVIEW_ALL = `
query getListReview($pageSize: Int, $currentPage: Int) {
  getListReview(pageSize: $pageSize, currentPage: $currentPage) {
    items {
      admin_response
      badges
      customer_avatar
      description
      images
      nickname
      order_created_at
      order_received_at
      rating_stars
      review_created_at
      review_id
      tag_image
      tag_name
      videos
    }
    page_info {
      current_page
      page_size
      total_pages
    }
    total_count
  }
}`
// GraphQL query để lấy chi tiết review theo ID
const GET_REVIEW_DETAIL = `
query GetReview($reviewId: Int!) {
  review(reviewId: $reviewId) {
    review_id
    product_id
    sku
    source
    author_name
    author_avatar
    rating
    title
    content
    is_verified_buyer
    status
    reviewed_at
    media {
      media_id
      type
      url
      thumbnail
    }
    comments {
      comment_id
      author_type
      author_name
      content
      status
      created_at
      replies {
        comment_id
        author_name
        content
        created_at
      }
    }
    reactions {
      reaction_id
      reaction
      count
    }
  }
}
`

// GraphQL query để lấy tổng quan đánh giá sản phẩm (summary)
const GET_MONA_PRODUCT_REVIEW_SUMMARY = `
query GetProductReviewSummary($productId: Int!) {
  productRatingSummary(productId: $productId) {
    product_id
    avg_rating
    total_review
    star_1
    star_2
    star_3
    star_4
    star_5
    last_sync_at
    updated_at
  }
}
`

// GraphQL query để lấy danh sách reviews có filter
const GET_PRODUCT_REVIEWS_FILTERED = `
query GetProductReviewsFiltered(
  $productId: Int!
  $pageSize: Int
  $currentPage: Int
  $source: String
  $rating: Int
  $status: String
  $sortBy: String
  $sortOrder: String
) {
  productReviews(
    productId: $productId
    pageSize: $pageSize
    currentPage: $currentPage
    source: $source
    rating: $rating
    status: $status
    sortBy: $sortBy
    sortOrder: $sortOrder
  ) {
    items {
      review_id
      product_id
      sku
      source
      external_review_id
      external_order_id
      author_name
      author_avatar
      rating
      title
      content
      is_verified_buyer
      status
      reviewed_at
      created_at
      updated_at
      media {
        media_id
        type
        url
        thumbnail
        created_at
      }
      comments {
        comment_id
        author_type
        author_name
        content
        status
        created_at
      }
      reactions {
        reaction_id
        reaction
        count
      }
    }
    total_count
    page_info {
      current_page
      page_size
      total_pages
    }
  }
}

`
// Interface cho review summary
export interface IReviewSummary {
  product_id: number
  avg_rating: number
  total_review: number
  star_1: number
  star_2: number
  star_3: number
  star_4: number
  star_5: number
  last_sync_at: string
  updated_at: string
}
/**
 * Lấy tổng quan đánh giá sản phẩm
 * @param productId - ID của sản phẩm
 * @returns Review summary data hoặc null nếu có lỗi
 */
export const getProductReviewSummary = async (productId: number): Promise<IReviewSummary | null> => {
  try {
    const response = await fetchWithToken({
      query: GET_MONA_PRODUCT_REVIEW_SUMMARY,
      variables: { productId },
    })

    const data = response?.payload?.data?.productRatingSummary

    if (response?.payload?.errors) {
      console.error('Error fetching review summary:', response.payload.errors[0]?.message)
      return null
    }

    return data || null
  } catch (error) {
    console.error('Error fetching product review summary:', error)
    return null
  }
}

/**
 * Lấy thông tin chi tiết của một review theo ID
 * @param reviewId - ID của review cần lấy
 * @returns Review detail data hoặc null nếu có lỗi
 */
export const getReviewDetail = async (reviewId: number): Promise<IReviewDetail | null> => {
  try {
    const response = await fetchWithToken({
      query: GET_REVIEW_DETAIL,
      variables: { reviewId },
    })

    const data = response?.payload?.data?.review

    if (response?.payload?.errors) {
      console.error('Error fetching review detail:', response.payload.errors[0]?.message)
      return null
    }

    return data || null
  } catch (error) {
    console.error('Error fetching review detail:', error)
    return null
  }
}

export const getAllReviews = async ({
  pageSize = 10,
  currentPage = 1,
}: {
  pageSize?: number
  currentPage?: number
}) => {
  try {
    const response = await fetcher.get(
      buildQueryV2(QUERY_LIST_REVIEW_ALL, {
        pageSize,
        currentPage,
      }),
      {
        next: {
          revalidate: 180,
          tags: ['list-all-reviews'],
        },
        cache: 'force-cache',
      }
    )

    if (response?.payload?.errors) {
      return {
        status: 'error',
        message: response.payload.errors[0]?.message,
        data: null,
      }
    }

    return {
      status: 'success',
      data: response?.payload?.data?.getListReview,
      message: '',
    }
  } catch (err) {
    console.error('Error fetching all reviews:', err)
    return {
      status: 'error',
      message: (err as Error).message,
      data: null,
    }
  }
}

export const getPostReviewByProductId = async ({ params, options, productId }: IListPostReviewProps) => {
  const { filter = {}, pageSize = PAGE_SIZE, currentPage = 1, search = '', sort = {} } = params
  try {
    const response = await fetcher.get(
      buildQueryV2(QUERY_POST_REVIEW, {
        filter,
        pageSize,
        currentPage,
        search,
        sort,
        productId,
      }),
      {
        next: { revalidate: 300, tags: [`post-review`, `${productId}`, `${JSON.stringify(params)}`] },
        cache: 'force-cache',
        locale: options?.locale,
      }
    )
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }
    const listPostData = response?.payload?.data?.getPostsByProductUrlKey as IListPostReview

    return { status: 'success', data: listPostData, message: '' }
  } catch (err) {
    console.error('Error fetching list posts review:', err)
    return { status: 'error', message: (err as Error)?.message, data: null }
  }
}

export const getListReview = async ({ params, options, productId }: IListPostReviewProps) => {
  const { pageSize = PAGE_SIZE, currentPage = 1, rating_stars, status } = params
  try {
    const response = await fetcher.get(
      buildQueryV2(GET_PRODUCT_REVIEWS_FILTERED, {
        productId,
        pageSize,
        currentPage,
        rating: rating_stars,
        status,
      }),
      {
        next: { revalidate: 180, tags: [`list-review`, `${productId}`, `${JSON.stringify(params)}`] },
        cache: 'force-cache',
        locale: options?.locale,
      }
    )
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }
    const listReview = response?.payload?.data?.productReviews as IListReview

    return { status: 'success', data: listReview, message: '' }
  } catch (err) {
    console.error('Error fetching list review:', err)
    return { status: 'error', message: (err as Error)?.message, data: null }
  }
}

export const getCustomerReviewSummary = async () => {
  try {
    const response = await fetchWithToken({
      query: QUERY_CUSTOMER_REVIEWS_SUMMARY,
    })

    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }
    const listReview = response?.payload?.data?.getCustomerReviewSummary as ICustomerReviewSummary

    return { status: 'success', data: listReview, message: '' }
  } catch (err) {
    console.error('Error fetching list reviews summary:', err)
    return { status: 'error', message: (err as Error)?.message, data: null }
  }
}

export const getListReviewsByCustomer = async ({ params, options }: IListReviewByCustomerProps) => {
  const { filter = {}, pageSize = PAGE_SIZE, currentPage = 1, search = '', sort = {}, status } = params
  try {
    const response = await fetchWithToken({
      query: QUERY_LIST_REVIEWS_BY_CUSTOMER,
      variables: {
        filter,
        pageSize,
        currentPage,
        status,
        // search,
        // sort,
      },
    })
    console.log('🚀 ~ getListReviewsByCustomer ~ response:', response)

    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }
    const listReview = response?.payload?.data?.getCustomerReviewsList as IListReviewByCustomer

    return { status: 'success', data: listReview, message: '' }
  } catch (err) {
    console.error('Error fetching list reviews by customer:', err)
    return { status: 'error', message: (err as Error)?.message, data: null }
  }
}

export const postReview = {
  get: getPostReviewByProductId,
  listPostReview: ({ params, productId }: IListPostReviewProps) => [
    `post-review`,
    `${productId}`,
    `${JSON.stringify(params)}`,
  ],
}

export const reviewService = {
  getListReview: getListReview,
  listReview: ({ params, productId }: IListPostReviewProps) => [
    `list-review`,
    `${productId}`,
    `${JSON.stringify(params)}`,
  ],
}
